// Sprint 9.5 — AnalyticsKPICard
// Card riusabile per dashboard analytics: label, valore, delta vs periodo prec.
//
// Pattern: chevron up/down con colore semantico (green/red). Tooltip on hover
// con valore precedente.

import { ReactNode, useState } from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

export interface AnalyticsKPICardProps {
  label: string;
  value: string;
  unit?: string;
  deltaPct?: number | null;
  prevValue?: string;
  invertDeltaColor?: boolean;   // se true, up = rosso (es. churn rate)
  icon?: ReactNode;
  iconBg?: string;
  iconColor?: string;
  hint?: string;
}

export function AnalyticsKPICard({
  label,
  value,
  unit,
  deltaPct,
  prevValue,
  invertDeltaColor = false,
  icon,
  iconBg = 'var(--green-soft, #e8f5ee)',
  iconColor = 'var(--green-deep, #0b8a3e)',
  hint,
}: AnalyticsKPICardProps) {
  const [hovered, setHovered] = useState(false);

  const isUp = deltaPct !== null && deltaPct !== undefined && deltaPct > 0;
  const isDown = deltaPct !== null && deltaPct !== undefined && deltaPct < 0;
  const isFlat = deltaPct !== null && deltaPct !== undefined && deltaPct === 0;
  const isUnknown = deltaPct === null || deltaPct === undefined;

  let deltaColor = 'var(--ink-3, #666)';
  if (isUp) deltaColor = invertDeltaColor ? '#c0392b' : '#0b8a3e';
  if (isDown) deltaColor = invertDeltaColor ? '#0b8a3e' : '#c0392b';

  return (
    <div
      style={{
        background: 'var(--surface, #fff)',
        border: '1px solid var(--line, #e5e5e5)',
        borderRadius: 14,
        padding: '18px 20px',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: iconBg,
          color: iconColor,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 12,
        }}
      >
        {icon}
      </div>
      <div
        style={{
          fontFamily: 'var(--mono, monospace)',
          fontSize: 10.5,
          fontWeight: 600,
          color: 'var(--mute, #888)',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--display, sans-serif)',
          fontWeight: 800,
          fontSize: 26,
          letterSpacing: -0.6,
          lineHeight: 1.1,
          color: 'var(--ink, #111)',
        }}
        className="tnum"
      >
        {value}
        {unit && (
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--ink-3, #666)',
              marginLeft: 2,
            }}
          >
            {unit}
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 500,
          color: deltaColor,
          marginTop: 6,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
        title={prevValue ? `Precedente: ${prevValue}` : ''}
      >
        {isUp && <ArrowUpRight size={12} strokeWidth={2.4} />}
        {isDown && <ArrowDownRight size={12} strokeWidth={2.4} />}
        {(isFlat || isUnknown) && <Minus size={12} strokeWidth={2.4} />}
        <span>
          {isUnknown ? 'Senza riferimento' : `${deltaPct! > 0 ? '+' : ''}${deltaPct}% vs periodo prec.`}
        </span>
      </div>
      {hint && hovered && (
        <div
          style={{
            position: 'absolute',
            bottom: 8,
            right: 12,
            fontSize: 10,
            color: 'var(--mute, #888)',
            fontStyle: 'italic',
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
